import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import ToastrService from '../../services/ui/toastrService';
import ToastrLevelsEnum from '../../services/enums/toastrLevelsEnum';
import DeleteDialog from '../portal/dialogs/deleteDialog';
import DividedList from '../portal/dividedList';
import ActionButton from '../portal/buttons/actionButton';
import Dialog from 'material-ui/Dialog';
import agent from '../../services/web/agent';
import { DialogHeader, Button } from '@checkmarx/cx-ui';
import EditUsersRolesDialog from './editBulkUsers/editUsersRolesDialog';

@inject('usersStore', 'usersGridStore', 'translationsStore', 'RolesCheckboxListStore') @observer
export default class SelectedUsersActions extends Component {

    constructor(props){
        super(props);
        this.t = this.props.translationsStore.translate;

        this.state = {
            currentActionInProgress: null,
            isOpenDeleteDialog: false,
            isOpenEditRoleDialog: false
        };

        this.actions = {
            DELETE : 'delete',
            ENABLE : 'enable',
            DISABLE : 'disable',
            EDIT_ROLES : 'edit-roles'
        }
    }

    actionButton = (onClick, text, action) => 
        <ActionButton 
            onClick={onClick} 
            text={text} 
            disabled={this.state.currentActionInProgress !== null}
            inProgress={action === this.state.currentActionInProgress} />;
    
    renderEnableUsers = () => this.actionButton(this.onEnableUsersClick, this.t('ENABLE'), this.actions.ENABLE);

    onEnableUsersClick = async () => {
        this.setState({currentActionInProgress : this.actions.ENABLE});
        const userIds = this.props.usersGridStore.selectedRows.map(user => user.id);
        try {
            await agent.Users.bulkUpdateStatus(true, userIds);
            await this.props.usersStore.getAllUsers();
            ToastrService.triggerToastr({level: ToastrLevelsEnum.SUCCESS, message: this.t('SUCCESSFULL_BULK_ENABLE_USERS')});
        } catch (err) {
            let msg = err && err.response && err.response.body && err.response.body.Message ? err.response.body.Message : err.message;
            ToastrService.triggerToastr({level: ToastrLevelsEnum.ERROR, message: this.t('FAILURE_BULK_ENABLE_USERS').replace('{errMsg}', msg)});   
        } finally {
            this.setState({currentActionInProgress : null});
        }
    }

    renderEditUsersRoles = () => this.actionButton(this.onEditUsersRolesClick, this.t('EDIT_ROLES'), this.actions.EDIT_ROLES);

    onEditUsersRolesClick = () => this.setState({isOpenEditRoleDialog : true});
    
    onCloseEditUsersRolesDialog = () => this.setState({ currentActionInProgress: null, isOpenEditRoleDialog: false});

    onEditRolesSubmitForm = async () => {

        this.setState({currentActionInProgress : this.actions.EDIT_ROLES})
        
        let selectedUsers = this.props.usersGridStore.selectedRows.map(user => user.id);

        let rolesToAdd = this.props.RolesCheckboxListStore.selectableAddRolesList
            .filter(role => role.selected).map(selectedRole => selectedRole.id);
        let rolesToRemove = this.props.RolesCheckboxListStore.selectableRemoveRolesList
            .filter(role => role.selected).map(selectedRole => selectedRole.id);
        
        const addRemoveRolesModel = {
            UsersIds: selectedUsers,
            RolesIdsToAdd: rolesToAdd,
            RolesIdsToRemove: rolesToRemove
        };

        try {
            const { unmodifiedUsers } = await agent.Users.bulkEditRoles(addRemoveRolesModel);
            let toastrMsg = this.props.translationsStore.translate('SUCCESSFULL_BULK_EDIT_ROLES');
            if(unmodifiedUsers.length > 0){
                toastrMsg = this.props.translationsStore.translate('PARTIALLY_SUCCESSFUL_BULK_EDIT_ROLES').replace('{usersEmails}', unmodifiedUsers.toString());
            }
            await this.props.usersStore.getAllUsers();
            ToastrService.triggerToastr({level: ToastrLevelsEnum.SUCCESS, message: toastrMsg, autoClose: unmodifiedUsers.length === 0 });
        } catch (err) {
            let msg = err && err.response && err.response.body && err.response.body.Message ? err.response.body.Message : err.message;
            ToastrService.triggerToastr({level: ToastrLevelsEnum.ERROR, message: this.props.translationsStore.translate('FAILURE_BULK_EDIT_ROLES').replace('{errMsg}', msg)});   
        } finally {
            this.setState({ currentActionInProgress: null, isOpenEditRoleDialog: false})        
        }
    }

    renderEditUsersRolesDialog = () =>  
        <EditUsersRolesDialog 
            onClose={this.onCloseEditUsersRolesDialog} 
            isOpenEditRolesDialog={this.state.isOpenEditRoleDialog}
            inProgress={this.state.currentActionInProgress === this.actions.EDIT_ROLES}
            onSubmit={this.onEditRolesSubmitForm}
        />;

    renderDisableUsers = () => this.actionButton(this.onDisableUsersClick, this.t('DISABLE'),this.actions.DISABLE);

    onDisableUsersClick = async () => {
        this.setState({currentActionInProgress : this.actions.DISABLE});
        const userIds = this.props.usersGridStore.selectedRows.map(user => user.id);
        try {
            await agent.Users.bulkUpdateStatus(false, userIds);
            await this.props.usersStore.getAllUsers(); 
            ToastrService.triggerToastr({level: ToastrLevelsEnum.SUCCESS, message: this.t('SUCCESSFULL_BULK_DISABLE_USERS')});
        } catch (err) {
            let msg = err && err.response && err.response.body && err.response.body.Message ? err.response.body.Message : err.message;
            ToastrService.triggerToastr({level: ToastrLevelsEnum.ERROR, message: this.t('FAILURE_BULK_DISABLE_USERS').replace('{errMsg}', msg)});
        } finally {
            this.setState({currentActionInProgress : null});
        }
    }
    renderDeleteUsers = () => this.actionButton(() => this.setState({isOpenDeleteDialog: true}), this.t('DELETE'));

    renderDeleteConfirmation = () => 
        <DeleteDialog
            show={this.state.isOpenDeleteDialog}
            onConfirm={() => this.onDeleteUsersClick()}
            deleteButtonText={this.t('DELETE')}
            cancelText={this.t('CANCEL')}
            closeDeleteDialog={() => this.setState({isOpenDeleteDialog: false})}
            titleText={this.t('DELETE_USERS')}
            contentText={this.t('BULK_DELETE_DIALOG_MESSAGE').replace('{numUsers}', this.props.usersGridStore.selectedRows.length)}
            dialogDataCy='delete-users'
            dataCy='delete-users-button'
            inProgress={this.state.currentActionInProgress === this.actions.DELETE}
        />

    onDeleteUsersClick = async () => {
        this.setState({currentActionInProgress : this.actions.DELETE});
        const users = this.props.usersGridStore.selectedRows;
        try {
            await agent.Users.bulkDelete(users.map(user => user.id)); 
            this.props.usersGridStore.selectedRows = [];
            await this.props.usersStore.getAllUsers();
            ToastrService.triggerToastr({level: ToastrLevelsEnum.SUCCESS, message: this.t('SUCCESSFULL_BULK_USERS_DELETE')});    
        } catch (err) {
            let msg = err && err.response && err.response.body && err.response.body.Message ? err.response.body.Message : err.message;
            this.setState({currentActionInProgress : null, isOpenDeleteDialog: false });
            ToastrService.triggerToastr({level: ToastrLevelsEnum.ERROR, message: this.t('FAILURE_BULK_USERS_DELETE').replace('{errMsg}', msg)}); 
        } 
    }      

    renderToolbar = () =>
        <DividedList>
            {this.renderEnableUsers()}
            {this.renderDisableUsers()}
            {this.renderEditUsersRoles()}
            {this.renderDeleteUsers()}
        </DividedList>;
    

    render(){

        const { selectedRows } = this.props.usersGridStore;
        return (<div className='users-actions-left-toolbar'>
                    <div className='users-selected'> {this.t('NUMBER_OF_USERS_SELECTED').replace('{numSelected}',selectedRows.length)} </div>  
                    <div className='users-actions-buttons'>
                        {this.renderToolbar()}
                    </div>
                    {this.renderDeleteConfirmation()}
                    {this.renderEditUsersRolesDialog()}
                </div>);
    }
}



// WEBPACK FOOTER //
// ./src/components/users/selectedUsersActions.jsx